import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Layers, ScanLine, Sparkles, FileOutput, Ruler,
  ArrowRight, CheckCircle2, Building2, FileText, Download
} from 'lucide-react';

const steps = [
  {
    icon: ScanLine,
    title: 'Scan & Upload',
    desc: 'Upload scanned paper drawings — PDF, PNG, JPG, or TIFF. Our pipeline handles faded, yellowed, and low-resolution scans.',
    accent: 'blue',
  },
  {
    icon: Sparkles,
    title: 'AI Enhancement',
    desc: 'OpenCV deskews, denoises, and boosts contrast. GPT-4o vision extracts room labels, dimensions, and annotations.',
    accent: 'sky',
  },
  {
    icon: Ruler,
    title: 'Review & Calibrate',
    desc: 'Verify detected rooms on an interactive overlay. Calibrate the drawing scale for accurate square footage calculations.',
    accent: 'emerald',
  },
  {
    icon: FileOutput,
    title: 'Export DXF + PDF',
    desc: 'Download AutoCAD-ready DXF files with room polylines and sq ft labels, plus professional PDF summary reports.',
    accent: 'violet',
  },
];

const features = [
  { icon: Building2, text: 'Room polygons with calculated square footage' },
  { icon: FileText, text: 'AutoCAD DXF files — open directly in CAD software' },
  { icon: Download, text: 'Full digitization package (DXF + PDF + JSON + images)' },
  { icon: Ruler, text: 'Manual scale calibration for precise measurements' },
  { icon: ScanLine, text: 'Handles 30+ year old faded & yellowed drawings' },
  { icon: CheckCircle2, text: 'Human-in-the-loop review before final export' },
];

const accentMap = {
  orange: 'border-primary/30 bg-blue-50 dark:bg-blue-950/10 text-primary',
  blue: 'border-blue-500/30 bg-blue-600/5 text-blue-400',
  emerald: 'border-emerald-500/30 bg-emerald-600/5 text-emerald-400',
  violet: 'border-violet-500/30 bg-violet-600/5 text-violet-400',
};

export default function DigitizeLandingPage() {
  const { user } = useAuth();
  const ctaLink = user ? '/digitize/upload' : '/login?redirect=digitize';

  return (
    <div className="min-h-screen bg-card noise-overlay" data-testid="digitize-landing">
      {/* Nav */}
      <nav className="sticky top-0 z-50 border-b border-border/50 bg-card/90 backdrop-blur-md">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 h-14">
          <Link to="/" className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-primary" />
            <span className="font-heading font-bold text-sm tracking-wider uppercase text-foreground">
              FMiQ<span className="text-primary">Intel</span>
            </span>
          </Link>
          <div className="flex items-center gap-4">
            {user ? (
              <Link
                to="/dashboard"
                data-testid="nav-dashboard-link"
                className="text-xs font-heading uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
              >
                Dashboard
              </Link>
            ) : null}
            <Link
              to={ctaLink}
              data-testid="nav-get-started-btn"
              className="flex items-center gap-1.5 border-l-2 border-primary bg-primary/10 hover:bg-primary/15 text-primary font-heading font-bold uppercase tracking-wider text-xs h-9 px-4 transition-colors"
            >
              Get Started <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50 via-transparent to-transparent" />
        <div className="relative max-w-6xl mx-auto px-6 pt-20 pb-16">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-primary/10 border border-primary/20 mb-6">
              <ScanLine className="w-3.5 h-3.5 text-primary" />
              <span className="font-heading font-bold text-[10px] uppercase tracking-widest text-primary">Paper-to-Digital Conversion</span>
            </div>
            <h1 className="font-heading font-black text-5xl md:text-7xl tracking-tighter text-white leading-[0.9] mb-6" data-testid="hero-title">
              Turn Old Blueprints<br />
              <span className="text-primary">Into Digital Assets</span>
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed max-w-xl mb-10">
              Got stacks of 30-year-old paper drawings? We scan, enhance, extract room data,
              calculate square footage, and export AutoCAD-ready DXF files — all for about
              <span className="text-white font-mono font-bold"> $1 per drawing set</span>.
            </p>
            <div className="flex items-center gap-4">
              <Link
                to={ctaLink}
                data-testid="hero-cta-btn"
                className="flex items-center gap-2 border-l-2 border-primary bg-primary/10 hover:bg-primary/15 text-primary font-heading font-bold uppercase tracking-wider text-sm h-12 px-8 transition-colors"
              >
                Start Digitizing <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#how-it-works"
                data-testid="hero-learn-more"
                className="flex items-center gap-2 rounded-sm border border-border bg-transparent hover:bg-secondary text-foreground/80 font-heading uppercase tracking-wider text-xs h-12 px-6 transition-colors"
              >
                How It Works
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="max-w-6xl mx-auto px-6 py-20">
        <div className="mb-12">
          <h2 className="font-heading font-bold text-lg uppercase tracking-tight text-foreground mb-2" data-testid="how-it-works-title">
            How It Works
          </h2>
          <p className="text-sm text-muted-foreground">Four steps from paper to structured digital data</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 stagger-children">
          {steps.map((step, i) => (
            <div
              key={i}
              className={`relative border rounded-sm p-6 ${accentMap[step.accent]} transition-colors hover:border-opacity-60`}
              data-testid={`step-card-${i}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-8 h-8 rounded-sm flex items-center justify-center text-xs font-mono font-bold ${
                  step.accent === 'orange' ? 'bg-primary/15 text-primary' :
                  step.accent === 'blue' ? 'bg-blue-600/20 text-blue-400' :
                  step.accent === 'emerald' ? 'bg-emerald-600/20 text-emerald-400' :
                  'bg-violet-600/20 text-violet-400'
                }`}>
                  {String(i + 1).padStart(2, '0')}
                </div>
                <step.icon className="w-5 h-5" />
              </div>
              <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-foreground mb-2">{step.title}</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-border bg-card/30">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="mb-12">
            <h2 className="font-heading font-bold text-lg uppercase tracking-tight text-foreground mb-2" data-testid="features-title">
              What You Get
            </h2>
            <p className="text-sm text-muted-foreground">Everything needed to bring paper drawings into the digital age</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((f, i) => (
              <div key={i} className="flex items-start gap-4 p-5 bg-card border border-border rounded-sm hover:border-border transition-colors" data-testid={`feature-card-${i}`}>
                <f.icon className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <p className="text-sm text-foreground/80 leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="bg-card border border-border rounded-sm p-8 md:p-12 flex flex-col md:flex-row items-start gap-10">
          <div className="flex-1">
            <h2 className="font-heading font-bold text-lg uppercase tracking-tight text-foreground mb-3" data-testid="pricing-title">
              Simple, Affordable Pricing
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6">
              AI-powered page classification means you only pay for pages that contain extractable data.
              Cover sheets and general notes are automatically skipped.
            </p>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-card border border-border rounded-sm p-4">
                <p className="font-heading text-[9px] uppercase tracking-widest text-muted-foreground mb-1">Per Page</p>
                <p className="font-mono text-xl font-bold text-foreground">~$0.04</p>
                <p className="text-[10px] text-muted-foreground mt-1">Floor plans & schedules</p>
              </div>
              <div className="bg-card border border-border rounded-sm p-4">
                <p className="font-heading text-[9px] uppercase tracking-widest text-muted-foreground mb-1">30-Page Set</p>
                <p className="font-mono text-xl font-bold text-amber-400">~$1.00</p>
                <p className="text-[10px] text-muted-foreground mt-1">Typical drawing set</p>
              </div>
              <div className="bg-card border border-border rounded-sm p-4">
                <p className="font-heading text-[9px] uppercase tracking-widest text-muted-foreground mb-1">Processing</p>
                <p className="font-mono text-xl font-bold text-blue-400">~8 min</p>
                <p className="text-[10px] text-muted-foreground mt-1">30-page turnaround</p>
              </div>
            </div>
          </div>
          <div className="md:w-72 shrink-0">
            <div className="bg-card border border-primary/30 rounded-sm p-6 text-center">
              <p className="font-heading font-bold text-xs uppercase tracking-wider text-primary mb-3">Ready to digitize?</p>
              <Link
                to={ctaLink}
                data-testid="pricing-cta-btn"
                className="w-full flex items-center justify-center gap-2 border-l-2 border-primary bg-primary/10 hover:bg-primary/15 text-primary font-heading font-bold uppercase tracking-wider text-sm h-11 px-6 transition-colors"
              >
                Upload Drawings <ArrowRight className="w-4 h-4" />
              </Link>
              <p className="text-[10px] text-muted-foreground mt-3">No commitment. Pay only for what you process.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-primary" />
            <span className="font-heading font-bold text-xs tracking-wider uppercase text-muted-foreground">
              FMiQ<span className="text-primary">Intel</span>
            </span>
          </div>
          <p className="text-[10px] font-mono text-muted-foreground">Paper-to-Digital Conversion Engine</p>
        </div>
      </footer>
    </div>
  );
}
